import { Outlet, Link, Navigate, useLocation, useNavigate } from "react-router";
import { useAdminAuth } from "./AdminAuthContext";
import { useAdminData } from "./AdminDataContext";
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Calendar,
  Settings,
  Users,
  BookOpen,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ChevronLeft,
  CreditCard,
  FileText,
  Inbox,
  Shield,
} from "lucide-react";
import { useState, useEffect } from "react";

const SIDEBAR_COLLAPSED_KEY = "admin-sidebar-collapsed";
import defaultLogoSvg from "../../../imports/MoechaLogo.svg";

const navItems = [
  { label: "Dashboard", icon: LayoutDashboard, path: "/admin", permission: null },
  { label: "Products", icon: Package, path: "/admin/products", permission: "products" as const },
  { label: "Orders", icon: ShoppingCart, path: "/admin/orders", permission: "orders" as const },
  { label: "Wholesale Inquiries", icon: Inbox, path: "/admin/wholesale-inquiries", permission: "wholesale_inquiries" as const },
  { label: "Workshops", icon: Calendar, path: "/admin/workshops", permission: "workshops" as const },
  { label: "Site Config", icon: Settings, path: "/admin/site-config", permission: "site_config" as const },
  { label: "Users", icon: Users, path: "/admin/users", permission: "users" as const },
  { label: "Journal", icon: BookOpen, path: "/admin/journal", permission: "journal" as const },
  { label: "Press Kit", icon: FileText, path: "/admin/press-kit", permission: "press_kit" as const },
  { label: "Payments", icon: CreditCard, path: "/admin/payments", permission: "site_config" as const },
  { label: "GDPR", icon: Shield, path: "/admin/gdpr", permission: "orders" as const },
];

export function AdminLayout() {
  const { user, isAuthenticated, logout, hasPermission } = useAdminAuth();
  const { siteConfig } = useAdminData();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });
  const logo = siteConfig.logo || defaultLogoSvg;

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(sidebarCollapsed));
    } catch {
      // ignore
    }
  }, [sidebarCollapsed]);

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  const visibleItems = navItems.filter(
    (item) => item.permission === null || hasPermission(item.permission)
  );

  const currentItem = visibleItems.find((item) => {
    if (item.path === "/admin") return location.pathname === "/admin";
    return location.pathname.startsWith(item.path);
  });

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const roleLabel: Record<string, string> = {
    admin: "Administrator",
    editor: "Editor",
    journal_editor: "Journal Editor",
  };

  return (
    <div className="min-h-screen flex" style={{ fontFamily: "'Jost', sans-serif" }}>
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#2D2A26] text-white flex flex-col transition-[width,transform] duration-300 lg:static lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } ${sidebarCollapsed ? "lg:w-[72px]" : ""}`}
      >
        {/* Logo */}
        <div className={`border-b border-white/10 flex-shrink-0 ${sidebarCollapsed ? "lg:px-3 lg:py-4" : "px-6 py-6"}`}>
          <div className="flex items-center justify-between">
            <Link to="/" className={`flex items-center gap-3 min-w-0 ${sidebarCollapsed ? "lg:justify-center lg:gap-0" : ""}`}>
              {!sidebarCollapsed && (
                <div className="w-10 h-10 flex items-center justify-center shrink-0">
                  {logo ? (
                    <img src={logo} alt="MOECHA" className="max-w-full max-h-full object-contain brightness-0 invert" />
                  ) : (
                    <span
                      className="text-white tracking-[0.12em]"
                      style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "12px" }}
                    >
                      MO
                    </span>
                  )}
                </div>
              )}
              {!sidebarCollapsed && (
                <div className="min-w-0">
                  <p className="text-white/90 tracking-[0.15em] uppercase" style={{ fontSize: "10px" }}>
                    MOECHA
                  </p>
                  <p className="text-[#C7A76C] tracking-[0.1em] uppercase" style={{ fontSize: "9px" }}>
                    Studio
                  </p>
                </div>
              )}
            </Link>
            <div className="flex items-center gap-1">
              <button
                className="hidden lg:flex text-white/50 hover:text-white p-1.5 rounded"
                onClick={() => setSidebarCollapsed((c) => !c)}
                title={sidebarCollapsed ? "Expand menu" : "Collapse menu"}
                aria-label={sidebarCollapsed ? "Expand menu" : "Collapse menu"}
              >
                {sidebarCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
              </button>
              <button
                className="lg:hidden text-white/60 hover:text-white"
                onClick={() => setSidebarOpen(false)}
              >
                <X size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className={`flex-1 overflow-y-auto py-4 space-y-1 ${sidebarCollapsed ? "lg:px-2" : "px-3"}`}>
          {visibleItems.map((item) => {
            const isActive =
              item.path === "/admin"
                ? location.pathname === "/admin"
                : location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                title={sidebarCollapsed ? item.label : undefined}
                className={`flex items-center rounded-lg transition-all duration-200 ${
                  sidebarCollapsed ? "lg:justify-center lg:px-0 lg:py-2.5" : "gap-3 px-4 py-2.5"
                } ${
                  isActive
                    ? "bg-[#6F7F63]/20 text-[#A7B39A]"
                    : "text-white/50 hover:text-white/80 hover:bg-white/5"
                }`}
                style={{ fontSize: "13px", fontWeight: 400 }}
              >
                <item.icon size={18} strokeWidth={1.5} className="shrink-0" />
                {!sidebarCollapsed && <span className="tracking-[0.05em] truncate">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* User */}
        <div className={`border-t border-white/10 flex-shrink-0 ${sidebarCollapsed ? "lg:px-2 lg:py-4" : "px-4 py-4"}`}>
          <div className={`flex items-center gap-3 mb-3 ${sidebarCollapsed ? "lg:flex-col lg:gap-2 lg:mb-2" : ""}`}>
            <div className="w-8 h-8 rounded-full bg-[#6F7F63]/30 flex items-center justify-center shrink-0">
              <span className="text-[#A7B39A]" style={{ fontSize: "11px" }}>
                {user?.name?.charAt(0) || "U"}
              </span>
            </div>
            {!sidebarCollapsed && (
              <div className="min-w-0">
                <p className="text-white/80 truncate" style={{ fontSize: "12px" }}>
                  {user?.name}
                </p>
                <p className="text-white/30" style={{ fontSize: "10px" }}>
                  {roleLabel[user?.role || "admin"]}
                </p>
              </div>
            )}
          </div>
          <button
            onClick={handleLogout}
            title="Sign out"
            className={`text-white/40 hover:text-[#8B5C58] transition-colors ${sidebarCollapsed ? "lg:flex lg:justify-center lg:w-full lg:px-0 lg:py-1.5" : "flex items-center gap-2 w-full px-2 py-1.5"}`}
            style={{ fontSize: "12px" }}
          >
            <LogOut size={14} strokeWidth={1.5} className="shrink-0" />
            {!sidebarCollapsed && <span>Sign out</span>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 bg-[#F7F5F1]">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-[#F7F5F1]/90 backdrop-blur-sm border-b border-[#E6E0D7] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              className="lg:hidden text-[#2D2A26] hover:text-[#6F7F63] transition-colors"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu size={22} />
            </button>
            <div className="flex items-center gap-2 text-[#6F675F]">
              <span style={{ fontSize: "12px" }}>Studio</span>
              {currentItem && (
                <>
                  <ChevronRight size={12} className="text-[#D8D1C7]" />
                  <span className="text-[#2D2A26]" style={{ fontSize: "12px" }}>
                    {currentItem.label}
                  </span>
                </>
              )}
            </div>
          </div>
          <Link
            to="/"
            className="text-[#6F675F] hover:text-[#6F7F63] transition-colors tracking-[0.15em] uppercase"
            style={{ fontSize: "10px" }}
          >
            View Site
          </Link>
        </header>

        {/* Content */}
        <div className="p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}